// RecipeVersionsSheet — the "Past versions" history for a recipe.
//
// Every in-place content save (manual edit, AI revise, restore)
// snapshots the prior version into recipe.versions[] (see
// applyVersionSnapshot). This sheet lists those snapshots newest-first
// so a user who told the kitchen "make it vegan" can get the omnivore
// original back with one tap. Restore is non-destructive: it snapshots
// the CURRENT version first, so you can bounce between iterations
// without ever losing one.
//
// Solid paper surface (NOT a frosted token) — learned from the
// see-through revise-sheet bug: COLOR.deep is a 42%-opaque underlay,
// useless as a modal base.

import { createPortal } from "react-dom";
import { FONT, COLOR } from "../lib/tokens";

function relativeTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!t) return "";
  const diff = (Date.now() - t) / 1000;
  if (diff < 60)     return "just now";
  if (diff < 3600)   return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)  return `${Math.floor(diff / 3600)}h ago`;
  const days = Math.floor(diff / 86400);
  if (days === 1)    return "yesterday";
  if (days < 7)      return `${days}d ago`;
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return `${days}d ago`; }
}

export default function RecipeVersionsSheet({ versions = [], onRestore, onClose }) {
  const list = Array.isArray(versions) ? versions : [];

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 340,
        background: "rgba(0,0,0,0.72)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 480, maxHeight: "86%",
          overflowY: "auto", WebkitOverflowScrolling: "touch",
          background: "#FBF7EF",
          borderTop: `1px solid ${COLOR.border}`,
          boxShadow: "0 -10px 36px rgba(40,28,12,0.30)",
          borderTopLeftRadius: 18, borderTopRightRadius: 18,
          padding: "20px 20px 18px", boxSizing: "border-box",
        }}
      >
        <div style={{ width: 42, height: 4, background: COLOR.border, borderRadius: 2, margin: "0 auto 14px" }} />
        <div style={{ fontFamily: FONT.mono, fontSize: 12, fontWeight: 600, color: COLOR.gold, letterSpacing: "0.14em" }}>
          PAST VERSIONS
        </div>
        <p style={{ fontFamily: FONT.sans, fontSize: 13, color: COLOR.muted, margin: "6px 0 14px", lineHeight: 1.5 }}>
          Every edit saves the version before it. Restore any one — your
          current version is kept too, so nothing is ever lost.
        </p>

        {list.length === 0 ? (
          <div style={{ padding: "30px 12px", textAlign: "center", fontFamily: FONT.sans, fontSize: 14, color: COLOR.muted }}>
            No past versions yet. The first time you edit or AI-revise
            this recipe, the original lands here.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {list.map((v, i) => (
              <div
                key={`${v.savedAt || i}`}
                style={{
                  padding: "12px 14px",
                  background: "rgba(255,255,255,0.6)",
                  border: `1px solid ${COLOR.border}`,
                  borderRadius: 12,
                  display: "flex", alignItems: "center", gap: 10,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: FONT.mono, fontSize: 12, fontWeight: 600, color: COLOR.dim, letterSpacing: "0.06em" }}>
                    {i === 0 ? "MOST RECENT" : `VERSION ${list.length - i}`} · {relativeTime(v.savedAt).toUpperCase()}
                  </div>
                  <div style={{
                    marginTop: 3, fontFamily: FONT.sans, fontSize: 13, color: COLOR.ink,
                    lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis",
                    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                  }}>
                    {v.summary || "Edited"}
                  </div>
                </div>
                <button
                  type="button"
                  className="mcm-focusable"
                  onClick={() => onRestore?.(v)}
                  style={{
                    flexShrink: 0, padding: "9px 14px",
                    background: COLOR.leaf, border: "none", borderRadius: 10,
                    fontFamily: FONT.mono, fontSize: 12, fontWeight: 700,
                    color: COLOR.night, letterSpacing: "0.06em",
                    cursor: "pointer", touchAction: "manipulation",
                  }}
                >
                  RESTORE
                </button>
              </div>
            ))}
          </div>
        )}

        <button
          type="button"
          className="mcm-focusable"
          onClick={onClose}
          style={{
            width: "100%", marginTop: 16, padding: "13px",
            background: COLOR.soil, border: `1px solid ${COLOR.border}`,
            borderRadius: 12, fontFamily: FONT.mono, fontSize: 12, color: COLOR.dim,
            letterSpacing: "0.08em", cursor: "pointer", touchAction: "manipulation",
          }}
        >
          CLOSE
        </button>
      </div>
    </div>,
    document.body,
  );
}
